import { useGetDeviceData } from '@/features/devices/hooks/useGetDeviceData'
import { DeviceStatusIcons, DeviceStatusTitles } from '@/features/devices/types/constants'
import { DeviceModel, DeviceStatus } from '@/features/devices/types/types'
import { formatDate } from '@/shared/lib/date'
import { DeviceState } from '@/shared/components'
import { DeviceIcon } from '../icons/DeviceIcon'
import { ItemLayout } from '@/shared/components'
import { BlockSkeleton } from '@/shared/components'
import { StatusLabel } from '../StatusLabel'
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getShortenId } from '@/shared/utils/getShortenId'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

export const DeviceBlock = ({
  id,
  data,
  isLoading,
}: {
  id?: string
  data?: DeviceModel
  isLoading?: boolean
}) => {
  const subtitleClasses = 'text-sm text-text-secondary'
  const columnClasses = 'flex flex-col gap-4 gap-[10px] md:gap-4'

  // Состояние коллапса - свернут по умолчанию
  const [isCollapsed, setIsCollapsed] = useState(true)

  const { deviceData, isLoading: isDeviceLoading } = useGetDeviceData({
    deviceId: id || '',
  })

  // Обработчик коллапса
  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  const currentId = id ? id : (data?.id ?? '')
  const loading = isLoading || isDeviceLoading

  // Определяем статус с учетом isDisabledByUser
  const currentDevice = data || deviceData
  const displayStatus = currentDevice.isDisabledByUser
    ? DeviceStatus.Disabled
    : currentDevice.status

  return loading ? (
    <BlockSkeleton />
  ) : (
    <div className="border border-card-border dark:border-card-border-dark rounded-[16px] flex flex-col px-6 py-8 md:pr-[120px]">
      {/* Заголовок с ссылкой и кнопкой коллапса */}
      <div className="flex items-center justify-between md:justify-start">
        {currentId ? (
          <Link to={`/devices/${currentId}`} className="flex items-center gap-2">
            <h3>Устройство</h3>
          </Link>
        ) : (
          <h3 className="flex-1">Устройство</h3>
        )}

        {/* Кнопка коллапса - видна всегда, но кликабельна только в мобильной версии */}
        <button
          onClick={handleToggleCollapse}
          className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors md:pointer-events-none md:opacity-60"
          aria-expanded={!isCollapsed}
          aria-controls="device-content"
          aria-label={isCollapsed ? 'Развернуть' : 'Свернуть'}
        >
          <ChevronRight
            className={`w-5 h-5 transition-transform duration-200 ${
              isCollapsed ? '' : 'rotate-90'
            }`}
          />
        </button>
      </div>

      {/* Контент с анимированным коллапсом */}
      <div
        id="device-content"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed
            ? 'max-h-0 opacity-0 md:max-h-screen md:opacity-100' // В десктопе всегда развернут
            : 'max-h-screen opacity-100'
        }`}
      >
        <div className="flex pt-6 md:flex-row flex-col justify-between items-start gap-6 md:gap-2 text-sm">
          <div className={`${columnClasses}`}>
            <span className={`${subtitleClasses}`}>Устройство</span>
            <ItemLayout
              icon={<DeviceIcon status={DeviceStatusIcons[displayStatus]} />}
              title={data?.name || deviceData.name}
              subtitle={
                <div className="flex flex-col">
                  <CopyBlock text={data?.id || deviceData.id} title={data?.id || deviceData.id} color="secondary">
                    {getShortenId(data?.id || deviceData.id)}
                  </CopyBlock>
                </div>
              }
            />
          </div>

          <div className={`${columnClasses}`}>
            <span className={`${subtitleClasses}`}>Статус</span>
            <StatusLabel status={DeviceStatusIcons[displayStatus]}>
              {DeviceStatusTitles[displayStatus]}
            </StatusLabel>
          </div>

          <div className={`${columnClasses}`}>
            <span className={`${subtitleClasses}`}>Последняя активность</span>
            <div>
              <span className="mr-1 text-text-secondary">Запущено:</span>
              <span className="font-klein font-medium">
                {!deviceData.lastPingedAt || deviceData.lastPingedAt === 'Нет данных'
                  ? 'Нет данных'
                  : formatDate(deviceData.lastPingedAt, 'long', {})}
              </span>
            </div>
          </div>

          <div className={`${columnClasses}`}>
            <span className={`${subtitleClasses}`}>Реквизиты в системе</span>
            <span className="font-klein font-medium">0</span>
          </div>

          <div className={`${columnClasses}`}>
            <span className={`${subtitleClasses}`}>Состояние</span>
            <DeviceState
              direction="col"
              battery={deviceData.battery}
              network={deviceData.network}
              status={deviceData.status}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
